import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";

const emptyForm = { name: "", email: "", password: "", role: "staff" };

export default function Staff() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const isMaster = user?.role === "master";
  const isAdmin = ["admin", "master"].includes(user?.role);

  const load = () => api.get("/staff").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) load(); }, [user]);

  if (!isAdmin)
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="staff-forbidden">
        <p className="font-semibold">Accesso riservato agli amministratori.</p>
      </div>
    );

  const save = async () => {
    if (!form.email || !form.password) { toast.error("Email e password sono obbligatorie"); return; }
    try {
      await api.post("/staff", form);
      toast.success("Account creato");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'account "${u.email}"?`)) return;
    try {
      await api.delete(`/staff/${u.id}`);
      toast.success("Account eliminato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="staff-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold">Staff</h1>
          <p className="text-[#6B7280] mt-1">Account del team: gli staff possono registrare vendite e movimenti, ma non modificare il menu.</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setOpen(true); }} data-testid="staff-add-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F] gap-2">
          <Plus size={18} weight="bold" /> Nuovo account
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
        <Table data-testid="staff-table">
          <TableHeader className="bg-[#FAF9F6]">
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Ruolo</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="hover:bg-stone-50" data-testid={`staff-row-${u.id}`}>
                <TableCell className="font-medium">{u.name || "—"}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Badge data-testid={`staff-role-badge-${u.id}`} className={`rounded-full ${u.role === "master" ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : u.role === "admin" ? "bg-[#D95D39]/10 text-[#BF4F2F] hover:bg-[#D95D39]/10" : "bg-stone-100 text-stone-700 hover:bg-stone-100"}`}>
                    {u.role === "master" ? "Master" : u.role === "admin" ? "Admin" : "Staff"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {u.id !== user.id && (u.role !== "master" || isMaster) && (
                    <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`staff-delete-${u.id}`} className="hover:bg-red-50 text-[#D32F2F]">
                      <Trash size={18} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="staff-dialog">
          <DialogHeader><DialogTitle>Nuovo account staff</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="staff-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="staff-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Password (min 6 caratteri)</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="staff-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (permessi limitati)</SelectItem>
                  <SelectItem value="admin">Admin (accesso completo)</SelectItem>
                  {isMaster && <SelectItem value="master">Master (controllo totale)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annulla</Button>
            <Button onClick={save} data-testid="staff-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">Crea account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
